/**
 * wsHardStop — tear down a y-websocket provider HARD on the first signal
 * that the server has rejected the room (overflow / circuit breaker /
 * user_in_recovery / unauthorized), and prevent any further reconnect
 * attempts.
 *
 * Why this exists (the chandler-pattern bug)
 * ──────────────────────────────────────────
 * y-websocket has an internal exponential-backoff reconnect loop. When the
 * server closes a connection with code 1008 ("oversized payload"), 4503
 * ("user_in_recovery"), or 4401 ("unauthorized" — Hocuspocus's onAuthenticate
 * hook rejected the room), y-websocket TREATS THIS LIKE A NETWORK BLIP and
 * starts trying to reconnect. The client immediately re-uploads the same
 * oversized state (or retries the same doomed, access-denied room). The
 * server kills it again. Repeat forever.
 *
 * The 4401 case is special: it also covers a merely-expired access token, not
 * just permanent access-denial (the close frame can't distinguish them). So a
 * 4401 hard-stop is RECOVERABLE — `wsTokenRefresher` calls
 * `rearmUnauthorizedHardStop()` after it rotates the token, giving the room one
 * more reconnect per token refresh. See that function for the full rationale.
 *
 * This is exactly what happened to chandler+marketing@rivereditor.com —
 * a 64 MiB local Y.Doc that the server rejected, then the client uploaded
 * again, ad infinitum. That single user session held the per-room circuit
 * breaker open for the entire org, blocking every other user from
 * connecting to the org room for 5+ minutes at a time.
 *
 * What this helper does
 * ─────────────────────
 *   - Listens for `connection-close` with code 1008 / code 4503 / reason
 *     'user_in_recovery' (matches the server's emitted close codes).
 *   - Listens for `authenticationFailed` with reason 'sub_mismatch' — emitted
 *     by HocuspocusWebsocketProvider when the server's onAuthenticate hook
 *     denies a per-user room (space:, space-chat:, window-position:,
 *     notification:) because the JWT's sub does not own it. Unlike the
 *     close-code cases above, the server does NOT close the socket on this
 *     path (see websocket-server/src/hocuspocus/auth.js), so without this
 *     listener the connection just sits there until Hocuspocus's own
 *     stale-connection heuristic recycles it — reconnecting, re-sending the
 *     same doomed auth, and getting denied again, forever.
 *   - Also tracks EVERY OTHER `authenticationFailed` reason (a room opened
 *     with no token yet, an expired token, or a transient server error —
 *     @hocuspocus/server collapses all of these to the same generic
 *     'permission-denied' reason string on the wire, so the client can't
 *     tell them apart). These are expected to self-heal — e.g. once
 *     `wsTokenRefresher` injects a token after login — so a single failure
 *     is not hard-stopped. But if the same provider keeps failing without
 *     ever syncing, that self-heal clearly isn't happening (e.g. a bug that
 *     stops the token from ever reaching this provider — see the
 *     `wsTokenRefresher` fix this pairs with), so a bounded counter trips a
 *     hard stop after `TRANSIENT_AUTH_FAILURE_LIMIT` consecutive failures
 *     within `TRANSIENT_AUTH_WINDOW_MS`, instead of retrying forever. The
 *     counter resets to zero the moment the provider actually syncs.
 *   - On the FIRST such close/failure (or once the transient-failure counter
 *     is exhausted): calls `provider.disconnect()` to tear down
 *     y-websocket's internal reconnect timer.
 *   - Sets a one-way "circuit broken" flag on the provider that intercepts
 *     `connect()` calls and refuses them. (We can't delete the method
 *     without breaking type contracts, so we wrap it.)
 *   - Notifies the registered handler with the close info so callers can
 *     surface a banner, log a Sentry event, etc.
 *   - Adds a Sentry breadcrumb noting the hard-stop fired.
 *
 * The handler is registered globally via `wsHardStop.onHardStop(...)` —
 * there's a single broadcast point so a UI banner component can subscribe
 * once and react to any room being shut down.
 *
 * Idempotent. Safe to attach multiple times to the same provider (the
 * second attach is a no-op).
 *
 * Reference: WS robustness plan, Phase 2A (headline fix).
 */

import type { WebsocketProvider } from 'y-websocket';
import * as SentryDefault from '@sentry/react';
import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('WS_HARD_STOP', { enabled: true });

const SENTRY_BREADCRUMB_CATEGORY = 'ws.hard_stop';

/** A close-code/reason pair that means "the server permanently rejected this connection." */
export interface HardStopTrigger {
	code: number | undefined;
	reason: string | undefined;
}

export interface HardStopEvent {
	/**
	 * Provider associated with the event. `null` for synthetic events
	 * (e.g. `idb_bloat_cleared`) where the cause originated outside the
	 * provider lifecycle and there is no specific WebsocketProvider to
	 * attribute the event to.
	 */
	provider: WebsocketProvider | null;
	roomShort: string;
	trigger: HardStopTrigger;
	/**
	 * Why we shut down. One of:
	 *   - 'oversized_payload'    server closed with code 1008
	 *   - 'user_in_recovery'     server closed with code 4503 / reason
	 *   - 'manual'               operator/programmatic close
	 *   - 'size_budget'          client wsSizeBudget tripped
	 *   - 'idb_bloat_cleared'    client cleared bloated IDB to allow
	 *                            recovery on next reload (no provider
	 *                            associated)
	 *   - 'idb_corrupt_cleared' client cleared an IndexedDB store that
	 *                            failed to decode (corrupt Yjs update) so
	 *                            the doc can re-sync from server canonical
	 *                            state (no provider associated)
	 *   - 'permanent_auth_denied' server's onAuthenticate hook permanently
	 *                            denied this room (JWT sub does not own it)
	 *   - 'transient_auth_exhausted' the provider kept failing authentication
	 *                            with a non-permanent reason (no token yet /
	 *                            expired token / server error) for more than
	 *                            `TRANSIENT_AUTH_FAILURE_LIMIT` consecutive
	 *                            attempts without ever syncing — bounded
	 *                            circuit breaker for what would otherwise be
	 *                            an infinite retry loop
	 */
	cause:
		| 'oversized_payload'
		| 'user_in_recovery'
		| 'unauthorized'
		| 'manual'
		| 'size_budget'
		| 'idb_bloat_cleared'
		| 'idb_corrupt_cleared'
		| 'permanent_auth_denied'
		| 'transient_auth_exhausted';
}

export type HardStopHandler = (event: HardStopEvent) => void;

interface SentryLike {
	addBreadcrumb(crumb: {
		category: string;
		message: string;
		level?: 'debug' | 'info' | 'warning' | 'error';
		data?: Record<string, unknown>;
	}): void;
}

interface HardStopState {
	tripped: boolean;
	cause: HardStopEvent['cause'] | null;
	trigger: HardStopTrigger | null;
	/** Consecutive non-permanent authenticationFailed count in the current window. */
	transientAuthFailureCount: number;
	/** Timestamp (ms) the current transient-auth-failure window started. */
	transientAuthWindowStart: number;
}

// WeakMap so that disposing of a provider naturally drops its hard-stop
// state too — no leak if a provider is GC'd without explicit cleanup.
const stateByProvider = new WeakMap<WebsocketProvider, HardStopState>();

// Bounded circuit breaker for authenticationFailed reasons that are NOT
// tagged permanent (see the class doc comment above). Chosen generously
// enough to ride out a normal login flow (token arrives within one or two
// Hocuspocus backoff cycles) while still bounding a genuinely stuck provider
// to a finite number of retries instead of forever.
export const TRANSIENT_AUTH_FAILURE_LIMIT = 5;
export const TRANSIENT_AUTH_WINDOW_MS = 60_000;

// Module-level set of subscribers — the banner UI subscribes once and gets
// notified for any provider that hard-stops.
const handlers = new Set<HardStopHandler>();

function shortRoom(roomName: string | undefined): string {
	if (!roomName) return '?';
	if (roomName.length <= 18) return roomName;
	return `${roomName.slice(0, 6)}…${roomName.slice(-10)}`;
}

function getRoomName(provider: WebsocketProvider): string {
	// y-websocket exposes `roomname` on the provider (lowercase).
	return (provider as unknown as { roomname?: string }).roomname ?? '?';
}

// y-websocket's typed `.on`/`.off` overloads only accept its own fixed event
// names ('status' | 'sync' | 'connection-close' | 'connection-error'), so
// 'authenticationFailed' — a HocuspocusWebsocketProvider-only event with no
// y-websocket equivalent — needs an untyped escape hatch. y-websocket
// instances never emit it, so subscribing is always a harmless no-op there.
function asGenericEmitter(provider: WebsocketProvider): {
	on(event: string, fn: (...args: unknown[]) => void): void;
	off(event: string, fn: (...args: unknown[]) => void): void;
} {
	return provider as unknown as ReturnType<typeof asGenericEmitter>;
}

function isOversizedClose(trigger: HardStopTrigger): boolean {
	return trigger.code === 1008;
}

function isUserInRecoveryClose(trigger: HardStopTrigger): boolean {
	return trigger.code === 4503 || trigger.reason === 'user_in_recovery';
}

// Hocuspocus's `onAuthenticate` hook closes with @hocuspocus/common's
// `Unauthorized` constant (code 4401) whenever the hook throws — including
// our room-ACL rejections ("Access denied"). Without this hard-stop, a
// client holding a stale/wrong room reference (e.g. a browser tab still
// pointed at an org the user was removed from) retries this doomed room
// forever via y-websocket's exponential backoff, hammering the WS server
// with repeated connect → 403 → reconnect cycles indefinitely.
function isUnauthorizedClose(trigger: HardStopTrigger): boolean {
	return trigger.code === 4401;
}

function classifyClose(trigger: HardStopTrigger): HardStopEvent['cause'] | null {
	if (isOversizedClose(trigger)) return 'oversized_payload';
	if (isUserInRecoveryClose(trigger)) return 'user_in_recovery';
	if (isUnauthorizedClose(trigger)) return 'unauthorized';
	return null;
}

// Reason string emitted by websocket-server/src/hocuspocus/auth.js for room
// prefixes tagged `permanentOnDeny` in roomAuth.js — the ONLY authentication
// failure reason that can never resolve by retrying with the same token.
// Every other onAuthenticate rejection (no token yet, expired token, ACL
// membership pending, internal error) is transient and must keep retrying.
const PERMANENT_AUTH_DENIAL_REASON = 'sub_mismatch';

function isPermanentAuthDenial(reason: unknown): boolean {
	return reason === PERMANENT_AUTH_DENIAL_REASON;
}

/**
 * Record a non-permanent `authenticationFailed` event and trip a bounded
 * hard stop once the same provider has failed
 * `TRANSIENT_AUTH_FAILURE_LIMIT` times in a row within
 * `TRANSIENT_AUTH_WINDOW_MS` without an intervening successful sync (which
 * resets the counter — see the `sync` listener in `attachHardStop`).
 */
function recordTransientAuthFailure(
	provider: WebsocketProvider,
	reason: unknown,
	sentry: SentryLike,
): void {
	const state = stateByProvider.get(provider);
	if (!state || state.tripped) return;

	const now = Date.now();
	if (now - state.transientAuthWindowStart > TRANSIENT_AUTH_WINDOW_MS) {
		state.transientAuthWindowStart = now;
		state.transientAuthFailureCount = 0;
	}
	state.transientAuthFailureCount += 1;

	if (state.transientAuthFailureCount < TRANSIENT_AUTH_FAILURE_LIMIT) return;

	fireHardStop(
		provider,
		'transient_auth_exhausted',
		{ code: undefined, reason: String(reason) },
		sentry,
	);
}

function broadcast(event: HardStopEvent): void {
	for (const handler of handlers) {
		try {
			handler(event);
		} catch (err) {
			log.warn('hard-stop handler threw:', err);
		}
	}
}

function fireHardStop(
	provider: WebsocketProvider,
	cause: HardStopEvent['cause'],
	trigger: HardStopTrigger,
	sentry: SentryLike,
): void {
	const state = stateByProvider.get(provider);
	// Idempotent — a second close after we already tripped is a no-op.
	if (!state || state.tripped) return;
	state.tripped = true;
	state.cause = cause;
	state.trigger = trigger;

	const roomShort = shortRoom(getRoomName(provider));
	log.error(
		`[wsHardStop] ${roomShort} HARD STOP cause=${cause} ` +
		`code=${trigger.code ?? '?'} reason=${trigger.reason ?? ''} ` +
		`— stopping reconnect loop`,
	);

	try {
		sentry.addBreadcrumb({
			category: SENTRY_BREADCRUMB_CATEGORY,
			message: `hard-stop room=${roomShort} cause=${cause} code=${trigger.code ?? '?'}`,
			level: 'error',
			data: { roomShort, cause, code: trigger.code, reason: trigger.reason },
		});
	} catch {
		// Sentry should never throw, but the safety code must never block on it.
	}

	// Tear down y-websocket's internal reconnect timer. After this,
	// y-websocket will not auto-reconnect because we ALSO blocked
	// `connect()` below.
	try { provider.disconnect(); } catch (err) {
		log.warn('error during hard-stop disconnect:', err);
	}

	broadcast({ provider, roomShort, trigger, cause });
}

/**
 * Attach hard-stop protection to a provider. Returns a dispose function.
 *
 * Calling this multiple times for the same provider is safe — the second
 * call returns the same dispose function and does not double-attach.
 */
export function attachHardStop(
	provider: WebsocketProvider,
	options: { sentry?: SentryLike } = {},
): () => void {
	const sentry = options.sentry ?? (SentryDefault as SentryLike);

	const existingState = stateByProvider.get(provider);
	if (existingState) {
		// Already attached — return a no-op disposer to avoid double-cleanup.
		return () => { /* noop */ };
	}

	stateByProvider.set(provider, {
		tripped: false,
		cause: null,
		trigger: null,
		transientAuthFailureCount: 0,
		transientAuthWindowStart: Date.now(),
	});

	// y-websocket fires `connection-close` with the underlying CloseEvent
	// on every disconnect — including the network-blip (1006) ones we DON'T
	// want to hard-stop on. Filter strictly on the codes the server uses
	// to signal "permanently rejected."
	const onConnectionClose = (closeEvent: { code?: number; reason?: string } | null | undefined) => {
		const trigger: HardStopTrigger = {
			code: closeEvent?.code,
			reason: closeEvent?.reason,
		};
		const cause = classifyClose(trigger);
		if (!cause) return;
		fireHardStop(provider, cause, trigger, sentry);
	};

	const onAuthenticationFailed = (reason: unknown) => {
		if (isPermanentAuthDenial(reason)) {
			fireHardStop(provider, 'permanent_auth_denied', { code: undefined, reason: String(reason) }, sentry);
			return;
		}
		recordTransientAuthFailure(provider, reason, sentry);
	};

	// A genuine sync means the most recent connect() authenticated
	// successfully — clear the transient-auth-failure counter so a room
	// that self-heals (e.g. a token becomes available) isn't left one
	// flaky reconnect away from tripping on unrelated future failures.
	const onSynced = () => {
		const state = stateByProvider.get(provider);
		if (!state) return;
		state.transientAuthFailureCount = 0;
		state.transientAuthWindowStart = Date.now();
	};

	provider.on('connection-close', onConnectionClose);
	asGenericEmitter(provider).on('authenticationFailed', onAuthenticationFailed);
	provider.on('sync', onSynced);

	// Wrap connect() to refuse calls after we've tripped. We can't define
	// a property setter on a y-websocket instance reliably (the prototype
	// method is non-configurable in some bundlers), so we shadow it on
	// the instance. y-websocket calls this.connect() internally too —
	// blocking it here is the primary way we stop the reconnect loop.
	const originalConnect = provider.connect.bind(provider);
	(provider as unknown as { connect: () => void }).connect = function gatedConnect() {
		const s = stateByProvider.get(provider);
		if (s && s.tripped) {
			log.warn(`[wsHardStop] ${shortRoom(getRoomName(provider))} blocked connect() — circuit broken (cause=${s.cause})`);
			return;
		}
		originalConnect();
	};

	let disposed = false;
	return () => {
		if (disposed) return;
		disposed = true;
		try { provider.off('connection-close', onConnectionClose); } catch { /* noop */ }
		try { asGenericEmitter(provider).off('authenticationFailed', onAuthenticationFailed); } catch { /* noop */ }
		try { provider.off('sync', onSynced); } catch { /* noop */ }
		// Restore the original connect — defensive, in case the caller
		// wants to recycle the provider somehow. In practice the provider
		// is destroyed shortly after dispose, so this rarely matters.
		(provider as unknown as { connect: () => void }).connect = originalConnect;
		stateByProvider.delete(provider);
	};
}

/**
 * Manually trip the hard-stop on a provider — used by `wsSizeBudget`
 * when an outbound update would exceed the budget, so we never even let
 * the server see the oversized payload.
 */
export function manuallyHardStop(
	provider: WebsocketProvider,
	cause: 'manual' | 'size_budget' = 'manual',
	options: { sentry?: SentryLike; reason?: string } = {},
): void {
	const sentry = options.sentry ?? (SentryDefault as SentryLike);
	const trigger: HardStopTrigger = { code: undefined, reason: options.reason ?? cause };
	fireHardStop(provider, cause, trigger, sentry);
}

/** True if a hard-stop has fired for this provider. */
export function isHardStopped(provider: WebsocketProvider): boolean {
	return Boolean(stateByProvider.get(provider)?.tripped);
}

/**
 * Clear a hard-stop that fired with cause 'unauthorized' or
 * 'transient_auth_exhausted' and reconnect the provider once. Called by
 * `wsTokenRefresher` after it rotates the auth token (on the `auth-refresh`
 * event).
 *
 * Why these two causes are recoverable
 * ─────────────────────────────────────
 * Hocuspocus closes with code 4401 for EVERY `onAuthenticate` rejection — both
 * the permanent case (org membership revoked → 'Access denied') AND the
 * transient case (the connection's access token simply expired →
 * 'Invalid or expired token'). The close frame carries only the generic
 * 'Unauthorized' reason, so the client can't tell them apart at close time.
 * 'transient_auth_exhausted' is the same ambiguity via the OTHER auth-failure
 * path — `authenticationFailed` (no close frame at all) — bounded by a
 * consecutive-failure counter instead of a single close code; see the class
 * doc comment.
 *
 * Hard-stopping immediately is right for stopping the reconnect storm,
 * but making it PERMANENT would kill live sync for an expired/missing token
 * until a full page reload — even though a refreshed token would reconnect
 * cleanly. So instead we let the token refresher give the room exactly one
 * more chance per token rotation:
 *   - expired/missing-token case → reconnect succeeds with the fresh token
 *     (self-heal).
 *   - genuine access-denied → the retry fails auth again and re-hard-stops
 *     (immediately for 'unauthorized'; after another
 *     `TRANSIENT_AUTH_FAILURE_LIMIT` attempts for 'transient_auth_exhausted').
 *     That's bounded to one attempt (or one counter's worth) per (infrequent)
 *     token rotation, NOT a storm.
 *
 * Other causes (oversized_payload / user_in_recovery) are NOT re-armed here —
 * a new token doesn't shrink an oversized doc or end recovery. 'size_budget'
 * has its own, narrower re-arm path: see `clearSizeBudgetHardStop` below.
 *
 * Returns true if the provider was re-armed and reconnected.
 */
export function rearmUnauthorizedHardStop(provider: WebsocketProvider): boolean {
	const state = stateByProvider.get(provider);
	if (!state || !state.tripped) return false;
	if (state.cause !== 'unauthorized' && state.cause !== 'transient_auth_exhausted') return false;

	state.tripped = false;
	state.cause = null;
	state.trigger = null;
	state.transientAuthFailureCount = 0;
	state.transientAuthWindowStart = Date.now();

	const roomShort = shortRoom(getRoomName(provider));
	log.log(`[wsHardStop] ${roomShort} re-arming after token refresh — reconnecting once`);
	try {
		// connect() is gated on state.tripped, which we just cleared, so this
		// goes through to y-websocket's real connect().
		provider.connect();
	} catch (err) {
		log.warn('error during hard-stop re-arm reconnect:', err);
	}
	return true;
}

/**
 * Clear a hard-stop that fired with cause 'size_budget', after `wsSizeBudget`
 * has independently re-verified the doc's REAL encoded size and found it
 * under budget. Only `wsSizeBudget` calls this, and only right after that
 * verification — never speculatively.
 *
 * Why 'size_budget' is re-armable but 'oversized_payload' is not
 * ────────────────────────────────────────────────────────────
 * 'size_budget' is the CLIENT's own pre-emptive guess, derived from a
 * monotonic update-log byte counter that never shrinks by itself. A room
 * with a lot of churn (many small remote broadcasts, local tombstones) can
 * cross that counter's threshold while the doc's actual encoded state is
 * nowhere near the limit — a false positive worth verifying and clearing.
 * 'oversized_payload' (server close code 1008) and 'user_in_recovery' (4503)
 * are the SERVER's own authoritative verdict on the real wire payload; there
 * is nothing for the client to "verify" away, so those stay permanent.
 *
 * Returns true if the provider was un-tripped and reconnected.
 */
export function clearSizeBudgetHardStop(provider: WebsocketProvider): boolean {
	const state = stateByProvider.get(provider);
	if (!state || !state.tripped || state.cause !== 'size_budget') return false;

	state.tripped = false;
	state.cause = null;
	state.trigger = null;

	const roomShort = shortRoom(getRoomName(provider));
	log.log(`[wsHardStop] ${roomShort} size-budget false positive verified — clearing hard-stop and reconnecting`);
	try {
		provider.connect();
	} catch (err) {
		log.warn('error during size-budget hard-stop clear reconnect:', err);
	}
	return true;
}

/** Subscribe to hard-stop events for any provider. Returns an unsubscribe fn. */
export function onHardStop(handler: HardStopHandler): () => void {
	handlers.add(handler);
	return () => handlers.delete(handler);
}

/**
 * Broadcast a synthetic hard-stop event NOT tied to any specific provider.
 *
 * Used by `idbBloatDetector` (and any future client-side recovery path)
 * to surface a banner-friendly event when a chandler-style condition is
 * detected and recovered from outside the provider lifecycle. Sets
 * `provider: null` on the broadcast event.
 *
 * Does NOT touch any provider state.
 */
export function broadcastSyntheticHardStop(
	event: Omit<HardStopEvent, 'provider'>,
): void {
	broadcast({ provider: null, ...event });
}

/** Test seam — clear all module-level state. */
export function _resetHardStopForTests(): void {
	handlers.clear();
}
